import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RevealOnView } from "@/components/Reveal";
import { AXIOM } from "@/constants/testIds";
import { ArrowRight } from "lucide-react";

const SERVICES = [
  {
    key: "cad",
    n: "S/01",
    tab: "CAD",
    title: "CAD Engineering",
    tag: "Design",
    lede: "From industrial concepts to manufacturing-ready production models - optimised for cost, performance and manufacturability. Class-A surfaces, GD&T and tolerance stack-ups included.",
    capabilities: [
      "Industrial Design",
      "Surface Modelling - Class-A & Production",
      "Assembly Design & GD&T",
      "Tolerance Stack-up Analysis",
      "2D Production Drawings",
    ],
    stack: ["SolidWorks", "CATIA", "Siemens NX", "Autodesk Inventor"],
    img: "https://images.unsplash.com/photo-1662097513309-bb191405bf28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxwcmVjaXNpb24lMjBlbmdpbmVlcmluZyUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzg0NzQ4ODc5fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    key: "cae",
    n: "S/02",
    tab: "CAE",
    title: "CAE Engineering",
    tag: "Simulation",
    lede: "Advanced simulation to validate performance, improve reliability and reduce development risk - long before a single part is cut. FEA, CFD, NVH, thermal and EM.",
    capabilities: [
      "FEA - Structural",
      "CFD - Fluid & Heat Transfer",
      "NVH - Noise, Vibration, Harshness",
      "Thermal Analysis",
      "Electromagnetic & Signal Integrity",
      "Mold Flow Analysis",
    ],
    stack: ["Ansys", "Simulia Abaqus", "Star-CCM+", "COMSOL", "HyperMesh", "ANSA"],
    img: "https://images.unsplash.com/photo-1769147339214-076740872485?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxwcmVjaXNpb24lMjBlbmdpbmVlcmluZyUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzg0NzQ4ODc5fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    key: "proto",
    n: "S/03",
    tab: "Prototyping",
    title: "Prototyping",
    tag: "Iterate",
    lede: "Rapid prototyping to validate design, function and manufacturability - from first concept to production-ready. FDM, SLA, SLS, laser and silicone tooling in-house.",
    capabilities: [
      "FDM · SLA · SLS Printing",
      "Laser Cutting",
      "Silicone Molding",
      "Rapid Tooling",
      "Design-for-manufacturing iterations",
    ],
    stack: ["Formlabs", "Prusa", "Bambu Lab", "Markforged", "Stratasys"],
    img: "https://images.pexels.com/photos/31137405/pexels-photo-31137405.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    key: "test",
    n: "S/04",
    tab: "Testing",
    title: "Testing",
    tag: "Validate",
    lede: "Comprehensive test & validation to ensure performance, reliability and compliance at every stage. Numbers, not opinions.",
    capabilities: [
      "Wind Tunnel & Flow Experimentation",
      "Experimental Modal Analysis",
      "Fatigue Analysis",
      "Microstructure Analysis",
      "Acoustic Noise Testing",
      "Reliability Testing",
    ],
    stack: ["In-house lab", "Certified partners"],
    img: "https://images.pexels.com/photos/8278915/pexels-photo-8278915.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    key: "mfg",
    n: "S/05",
    tab: "Manufacturing",
    title: "Manufacturing",
    tag: "Ship",
    lede: "End-to-end manufacturing with precision, quality and scalability - pilot runs to full production, across 10+ vetted partners.",
    capabilities: [
      "CNC Machining",
      "Hydraulic Press Brake",
      "Structural Welding",
      "Heavy-duty Machining",
      "Injection Molding",
      "Deep Drawing & Stamping",
    ],
    stack: ["10+ manufacturing partners"],
    img: "https://images.pexels.com/photos/34718922/pexels-photo-34718922.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function Services() {
  const [activeKey, setActiveKey] = useState("cad");
  const active = SERVICES.find((s) => s.key === activeKey) || SERVICES[0];

  return (
    <section id="services" data-testid={AXIOM.services.root} className="bg-bg py-24 md:py-32 border-t border-rule">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <RevealOnView>
          <div className="grid grid-cols-12 gap-6 items-end mb-10">
            <div className="col-span-12 md:col-span-6">
              <div className="eyebrow mb-4">Capabilities · 05</div>
              <h2
                className="font-display text-ink"
                style={{ fontSize: "clamp(30px, 4vw, 46px)", lineHeight: 1.05, letterSpacing: "-0.02em" }}
              >
                Five disciplines. <span className="text-forge">One partner.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8">
              <p className="text-ink/70 text-[16px] md:text-[18px] leading-[1.55]">
                A dedicated engineering team that accelerates development, reduces
                technical risk and takes your product from concept to customer -
                without the overhead of building it in-house.
              </p>
            </div>
          </div>
        </RevealOnView>

        {/* Horizontal tab bar */}
        <RevealOnView delay={0.05}>
          <div
            role="tablist"
            aria-label="Service capabilities"
            className="grid grid-cols-5 border border-ink"
          >
            {SERVICES.map((s) => {
              const isActive = s.key === activeKey;
              return (
                <button
                  key={s.key}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveKey(s.key)}
                  data-testid={AXIOM.services.item(s.key)}
                  className={`relative flex items-center justify-center gap-2 px-3 md:px-6 py-5 md:py-6 border-l first:border-l-0 border-ink transition-colors duration-200 group ${
                    isActive
                      ? "bg-ink text-white"
                      : "bg-white text-ink hover:bg-panel"
                  }`}
                >
                  <span
                    className={`hidden md:inline font-mono text-[10px] tracking-[0.22em] ${
                      isActive ? "text-forge" : "text-smoke"
                    }`}
                  >
                    {s.n}
                  </span>
                  <span
                    className={`font-display text-[16px] md:text-[22px] leading-none tracking-[-0.02em] font-semibold ${
                      isActive ? "text-white" : "text-ink"
                    }`}
                  >
                    {s.tab}
                  </span>
                  {isActive && (
                    <motion.span
                      layoutId="tab-underline"
                      className="absolute -bottom-[1px] left-0 right-0 h-[3px] bg-forge"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </RevealOnView>

        {/* Panel */}
        <div className="border border-t-0 border-ink bg-white">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-12 gap-0"
            >
              {/* Image */}
              <div className="col-span-12 md:col-span-6 relative">
                <div className="relative w-full aspect-[4/3] md:aspect-auto md:h-full min-h-[340px] overflow-hidden border-r border-ink">
                  <img
                    src={active.img}
                    alt={active.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-ink/40 via-transparent to-transparent" />
                  <div className="absolute top-5 left-5 font-mono text-[10px] tracking-[0.16em] uppercase text-white/90">
                    {active.n} · Fig. {active.tab}
                  </div>
                  <div className="absolute bottom-5 left-5 flex items-center gap-2">
                    <span className="w-2 h-2 bg-forge inline-block" />
                    <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-white/90">
                      {active.tag}
                    </span>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="col-span-12 md:col-span-6 p-8 md:p-12 flex flex-col justify-between">
                <div>
                  <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-forge mb-4">
                    {active.n} - {active.tag}
                  </div>
                  <h3
                    className="font-display text-ink"
                    style={{ fontSize: "clamp(28px, 3.2vw, 44px)", lineHeight: 1.05, letterSpacing: "-0.02em" }}
                  >
                    {active.title}
                  </h3>
                  <p className="text-ink/75 mt-5 max-w-[54ch]" style={{ fontSize: "16px", lineHeight: 1.55 }}>
                    {active.lede}
                  </p>

                  <ul className="mt-8 divide-y divide-rule border-t border-rule">
                    {active.capabilities.map((c) => (
                      <li
                        key={c}
                        className="flex items-center gap-4 py-3 text-ink text-[15px]"
                      >
                        <span className="font-mono text-[10px] tracking-[0.14em] text-smoke w-6">
                          ·
                        </span>
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-between gap-6">
                  <div className="flex flex-wrap gap-2">
                    {active.stack.map((t) => (
                      <span
                        key={t}
                        className="border border-rule px-3 py-1 font-mono text-[10px] tracking-[0.14em] uppercase text-smoke"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 font-sans text-[13px] font-semibold text-ink hover:text-forge transition-colors group"
                  >
                    Discuss a {active.tab.toLowerCase()} brief
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
